import React, { useEffect, useMemo, useState } from "react";

import { Path, PathValue, useForm } from "react-hook-form";

import { PlusIcon, TrashIcon } from "@heroicons/react/outline";

import TinyMCE from "../Shared/TinyMCE";
import LoadingWheel from "../Shared/LoadingWheel";
import ConfirmDelete from "../Shared/ConfirmDelete";
import { ButtonInput } from "../Shared/Inputs/ButtonInput";
import { useCTABanners } from "../../data/ctaBanners/useCTABanners";
import { AddMarketingGroupFlyout } from "./AddMarketingGroupFlyout";
import useBlogPostCategories from "../../data/useBlogPostCategories";
import { StyledTextInput } from "../Shared/StyledInputs/StyledTextInput";
import StyledSwitchInput from "../Shared/StyledInputs/StyledSwitchInput";
import { StyledTimeInput } from "../Shared/StyledInputs/StyledTimeInput";
import { useEmailSettings } from "../../data/emailSettings/useEmailSettings";
import { StyledSelectInput } from "../Shared/StyledInputs/StyledSelectInput";
import { useMarketingGroups } from "../../data/marketingGroup/useMarketingGroups";
import { MultiLineSettingRow, SingleLineSettingRow } from "../Shared/SettingsRows";
import { StyledMultiSelectInput } from "../Shared/StyledInputs/StyledMultiSelectInput";
import { useEmailSettingsMutations } from "../../data/emailSettings/useEmailSettingsMutations";
import { StyledRadioButtonGroupInput } from "../Shared/StyledInputs/StyledRadioButtonGroupInput";
import { EmailSettingsMarketingGroupType, EmailSettingsUpdate, NewsletterArticleCategoryUpdate, NewsletterSchedule } from './../../models/emailSettings/EmailSettings';
import useAgencyMedia from "../../data/useAgencyMedia";
import { SubstitutionDropdownButton } from "../Shared/SubstitutionDropdownButton";
import { convertSubsToNested } from './../../utils/convertSubsToMergeTags';
import { SendTestModal } from "./SendTestModal";

interface CTAPlaceholderProps {
  recommendedResolution?: [number, number];
}

function CTAPlaceholder({ recommendedResolution = [300, 250] }: CTAPlaceholderProps) {
  return (
    <div className="border-dashed border-2 border-gray-300 rounded-md h-48 flex items-center justify-center mx-8 mt-2">
      <p className="text-center font-semibold text-gray-500">Recommended Resolution <br /> {recommendedResolution[0]}px x {recommendedResolution[1]}px</p>
    </div>
  );
}

interface NewsletterSettingsFormFields {
  enableNewsletter: boolean;
  newsletterSubject: string;
  newsletterFromName: string;
  newsletterIntro: string;
  newsletterArticleEnabled: boolean;
  newsletterArticleCount: string;
  newsletterPropertyEnabled: boolean;
  newsletterPropertyCount: string;
  newsletterTestimonialEnabled: boolean;
  newsletterTestimonialCount: string;

  newsletterSchedule: string;
  newsletterScheduleDayOfMonth?: string;
  newsletterScheduleTime?: string; // ISO time string

  newsletterArticleCategories: string[];
  ctaBanners: string[];
  marketingGroups: string[];

  topCtaEnabled: boolean;
  bottomCtaEnabled: boolean;
}

interface NewsletterSettingsProps {
  agencyId: string;
}

const subjectSubs = [
  { label: "Day", value: "date.Day" },
  { label: "Month", value: "date.Month" },
  { label: "Year", value: "date.Year" },
  { label: "Name", value: "agency.Name" },
  { label: "Domain", value: "agency.Domain" },
  { label: "First Name", value: "person.firstName" },
  { label: "Last Name", value: "person.lastName" },
]

export default function NewsletterSettings({ agencyId }: NewsletterSettingsProps) {
  const defaultGroups = ["68ea0ff2-a368-4d61-007d-08ddacd93827", "adb3bf1b-96aa-4b41-007e-08ddacd93827"];
  const { categories } = useBlogPostCategories();
  const { marketingGroups } = useMarketingGroups();
  const { ctaBanners } = useCTABanners(agencyId);
  const { emailSettings } = useEmailSettings(agencyId);
  const { media } = useAgencyMedia();
  const { update } = useEmailSettingsMutations(agencyId);
  const [sendTestOpen, setSendTestOpen] = useState(false);
  const { register, watch, setValue, getValues, reset, handleSubmit } =
    useForm<NewsletterSettingsFormFields>({
      defaultValues: {
        marketingGroups: defaultGroups,
        newsletterScheduleDayOfMonth: "1",
        newsletterSchedule: NewsletterSchedule.FirstDay.toString(),
      }
    });
  const [addGroupOpen, setAddGroupOpen] = useState(false);
  const [deleteGroupOpen, setDeleteGroupOpen] = useState(false);
  const [deleteGroupId, setDeleteGroupId] = useState<string>();

  const getCTABannersInOrder = () => {
    const ctaBanners = Array.from({ length: 3 }).map((_, index) => {
      const existingBanner = emailSettings.data?.data?.ctaBanners?.find((b: any) => b.order === index);
      return existingBanner ? existingBanner.ctaBannerId : "";
    });

    return ctaBanners;
  }

  const sortedCtaBanners = useMemo(() => {
    return ctaBanners.data?.data.sort((a, b) => {
      if (b.created < a.created) return -1;
      if (b.created > a.created) return 1;
      return 0;
    });
  }, [ctaBanners.data?.data]);

  useEffect(() => {
    if (!emailSettings.isLoading && emailSettings.data?.data) {
      console.log("emailSettings.data", emailSettings.data.data);

      let newsletterScheduleTime = emailSettings.data?.data.newsletterScheduleTime || "09:00";
      if (/^\d{2}:\d{2}:\d{2}$/.test(newsletterScheduleTime)) {
        newsletterScheduleTime = newsletterScheduleTime.substring(0, 5);
      }

      const ctaBanners = getCTABannersInOrder();

      const topCtaEnabled = (ctaBanners[0] !== "" || ctaBanners[1] !== "");
      const bottomCtaEnabled = (ctaBanners[2] !== "");

      reset({
        ...emailSettings.data.data as unknown as NewsletterSettingsFormFields,
        newsletterArticleCategories: emailSettings.data?.data.newsletterArticleCategories.length === 0 ? ["all-categories"] : emailSettings.data?.data.newsletterArticleCategories.map((c) => c.categoryId) || [],
        newsletterArticleCount: emailSettings.data?.data.newsletterArticleCount?.toString() ?? "1",
        ctaBanners: ctaBanners,
        topCtaEnabled,
        bottomCtaEnabled,
        newsletterPropertyCount: emailSettings.data?.data.newsletterPropertyCount?.toString() || "1",
        newsletterTestimonialCount: emailSettings.data?.data.newsletterTestimonialCount?.toString() || "1",
        marketingGroups: emailSettings.data?.data.marketingGroups.map((mg) => mg.marketingGroupId) || defaultGroups,
        newsletterSchedule: emailSettings.data?.data.newsletterSchedule.toString(),
        newsletterScheduleDayOfMonth: emailSettings.data?.data.newsletterScheduleDayOfMonth?.toString() || "1",
        newsletterScheduleTime
      });
    }
  }, [emailSettings.data, emailSettings.isLoading, reset]);

  const handleUpdate = (data: NewsletterSettingsFormFields) => {
    if (!emailSettings.data?.data) return;

    const existingCategories = emailSettings.data.data.newsletterArticleCategories || [];
    const articleCategories: NewsletterArticleCategoryUpdate[] = data.newsletterArticleCategories.map((categoryId) => {
      const matched = existingCategories.find((ec) => ec.categoryId === categoryId);
      return matched
        ? { id: matched.id, categoryId }
        : { categoryId };
    });

    const existingBanners = emailSettings.data.data.ctaBanners || [];
    const ctaBanners = data.ctaBanners.map((ctaBannerId, index) => {
      const matched = existingBanners.find((eb) => eb.ctaBannerId === ctaBannerId);
      return matched
        ? { id: matched.id, ctaBannerId, order: index }
        : { ctaBannerId, order: index };
    }).filter(b => b.ctaBannerId !== "");

    const existingGroups = emailSettings.data.data.marketingGroups || [];
    const marketingGroups = data.marketingGroups.map((groupId) => {
      const matched = existingGroups.find((eg) => eg.marketingGroupId === groupId);
      return matched
        ? { id: matched.id, marketingGroupId: groupId, type: matched.type }
        : { marketingGroupId: groupId, type: EmailSettingsMarketingGroupType.Newsletter };
    });

    let newsletterScheduleTime = data.newsletterScheduleTime || "09:00";
    if (/^\d{2}:\d{2}$/.test(newsletterScheduleTime)) {
      newsletterScheduleTime += ":00";
    }

    const updatedSettings: EmailSettingsUpdate = {
      ...emailSettings.data.data,
      ...data,
      newsletterArticleCount: parseInt(data.newsletterArticleCount),
      newsletterPropertyCount: parseInt(data.newsletterPropertyCount),
      newsletterTestimonialCount: parseInt(data.newsletterTestimonialCount),
      ctaBanners,
      marketingGroups,
      newsletterSchedule: parseInt(data.newsletterSchedule),
      newsletterScheduleDayOfMonth: data.newsletterScheduleDayOfMonth ? parseInt(data.newsletterScheduleDayOfMonth) : undefined,
      newsletterScheduleTime,
      newsletterArticleCategories: data.newsletterArticleCategories.includes("all-categories") ? [] : articleCategories,
    };

    update.mutate(updatedSettings);
  }

  if (emailSettings.isLoading || ctaBanners.isLoading || marketingGroups.isLoading || categories.isLoading || media.isLoading) {
    return (
      <div className="py-4 flex justify-center">
        <LoadingWheel width="w-8" height="h-8" />
      </div>
    )
  }

  return (
    <>
      {/* @ts-ignore */}
      <ConfirmDelete
        itemName="Marketing Group"
        open={deleteGroupOpen}
        setOpen={setDeleteGroupOpen}
        onConfirm={() => {
          if (!deleteGroupId) return;

          const currentGroups = watch("marketingGroups");
          setValue("marketingGroups", currentGroups.filter((id) => id !== deleteGroupId));
          setDeleteGroupOpen(false);
          setDeleteGroupId(undefined);
        }}
      />
      <AddMarketingGroupFlyout
        open={addGroupOpen}
        setOpen={setAddGroupOpen}
        existingGroups={watch("marketingGroups")}
        onAdd={(groupId: string) => {
          const currentGroups = watch("marketingGroups");
          setValue("marketingGroups", [...currentGroups, groupId]);
          setAddGroupOpen(false);
        }}
      />
      <SendTestModal
        open={sendTestOpen}
        setOpen={setSendTestOpen}
      />
      <div>
        <div className="pb-4 border-b">
          <h3 className="text-xl font-medium text-gray-900">
            Newsletter Settings
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Configure your newsletter settings here
          </p>
        </div>
        <form onSubmit={handleSubmit(handleUpdate)}>
          {emailSettings.isLoading || categories.isLoading || ctaBanners.isLoading || marketingGroups.isLoading || media.isLoading ? (
            <ul className="border border-gray-200 bg-white rounded-md divide-y divide-gray-200 my-4">
              <li key="settings-loading" className="py-4 flex justify-center">
                <LoadingWheel width="w-8" height="h-8" />
              </li>
            </ul>
          ) :
            <>
              <ul className="border border-gray-200 bg-white rounded-md divide-y divide-gray-200 my-4">
                <SingleLineSettingRow
                  label="Monthly Newsletter"
                  value={<StyledSwitchInput
                    register={register}
                    registerName="enableNewsletter"
                    watch={watch}
                    setValue={setValue}
                  />
                  }
                />
              </ul>
              {watch("enableNewsletter") ?
                (
                  <>
                    <ul className="border border-gray-200 bg-white rounded-md divide-y divide-gray-200 my-4">
                      <MultiLineSettingRow
                        label="Email Subject Line"
                        value={
                          <div className="w-full -mt-2 flex flex-row items-center space-x-2">
                            <StyledTextInput
                              colsClass=""
                              register={register}
                              registerName="newsletterSubject"
                            />
                            <SubstitutionDropdownButton
                              substitutions={subjectSubs}
                              onSelect={(value) => {
                                const current = getValues("newsletterSubject") || "";
                                const newValue = current + `{{${value}}}`;
                                setValue("newsletterSubject", newValue);
                              }}
                            />
                          </div>
                        }
                      />
                      <MultiLineSettingRow
                        label="Newsletter Email From Name"
                        value={
                          <StyledTextInput
                            colsClass=""
                            classes="w-full -mt-1"
                            register={register}
                            registerName="newsletterFromName"
                          />
                        }
                      />
                      <MultiLineSettingRow
                        label="Newsletter Intro"
                        description="The introduction text that will appear at the top of every newsletter sent. You can use merge tags to personalise it"
                        value={
                          <TinyMCE
                            // @ts-ignore
                            value={getValues("newsletterIntro")}
                            // @ts-ignore
                            onChange={(intro: string) => setValue("newsletterIntro", intro)}
                            // @ts-ignore
                            height={300}
                            mergeTags={convertSubsToNested(subjectSubs)}
                          />
                        }
                      />
                    </ul>
                    <ul className="border border-gray-200 bg-white rounded-md divide-y divide-gray-200 my-4">
                      <SingleLineSettingRow
                        label="Articles"
                        description="Include your latest blog articles in your monthly newsletter"
                        value={
                          <StyledSwitchInput
                            register={register}
                            registerName="newsletterArticleEnabled"
                            watch={watch}
                            setValue={setValue}
                          />
                        }
                      />
                      {watch("newsletterArticleEnabled") && (
                        <>
                          <MultiLineSettingRow
                            label=""
                            value={
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full divide-x divide-gray-200">
                                <MultiLineSettingRow
                                  classes="-mx-3 -my-2"
                                  label="Categories"
                                  value={
                                    <StyledMultiSelectInput
                                      classes="w-full -mt-2"
                                      register={register}
                                      registerName="newsletterArticleCategories"
                                      setValue={setValue}
                                      watch={watch}
                                      noneSelectedText="All Categories"
                                      onChange={(value) => {
                                        if (value.includes("all-categories")) {
                                          setValue("newsletterArticleCategories", ["all-categories"]);
                                        }
                                      }}
                                      options={[
                                        {
                                          label: "All Categories", value: "all-categories"
                                        },
                                        ...categories.data.map((c: any) => ({
                                          label: c.name,
                                          value: c.id,
                                        }))]}
                                    />
                                  }
                                />
                                <MultiLineSettingRow
                                  classes="-mx-3 -my-2"
                                  label="Number of Articles"
                                  value={
                                    <StyledSelectInput
                                      classes="w-full -mt-1"
                                      register={register}
                                      registerName="newsletterArticleCount"
                                      setValue={setValue}
                                      watch={watch}
                                      options={
                                        Array.from({ length: 8 })
                                          .map((_, index) => ({
                                            label: (index + 1).toString(),
                                            value: (index + 1).toString()
                                          }))
                                      }
                                    />
                                  }
                                />
                              </div>
                            }
                          />
                        </>
                      )}
                    </ul>
                    {watch("newsletterPropertyEnabled") && (
                      <ul className="border border-gray-200 bg-white rounded-md divide-y divide-gray-200 my-4">
                        <SingleLineSettingRow
                          label="Dual Call-to-Action Banners"
                          description="This will display two small display banners."
                          value={
                            <StyledSwitchInput
                              register={register}
                              registerName="topCtaEnabled"
                              watch={watch}
                              setValue={setValue}
                              onChange={(checked) => {
                                if (!checked) {
                                  setValue("ctaBanners.0", "");
                                  setValue("ctaBanners.1", "");
                                }
                              }}
                            />
                          }
                        />
                        {watch("topCtaEnabled") && (
                          <div className="grid grid-cols-1 md:grid-cols-2 divide-x divide-gray-200">
                            {Array.from({ length: 2 }).map((_, index) => {
                              const ctaMedia = media.data.find((m: any) => m.id === ctaBanners.data?.data.find((b) => b.id === watch(`ctaBanners.${index}`))?.mediaId);

                              return (
                                <SingleLineSettingRow
                                  key={index}
                                  label={`Top CTA Banner ${index + 1}`}
                                  valueContainerClassName="!w-4/6"
                                  value={
                                    <StyledSelectInput
                                      classes="w-full"
                                      register={register}
                                      registerName={`ctaBanners.${index}`}
                                      setValue={setValue}
                                      watch={watch}
                                      options={[{ label: "None", value: "" }, ...sortedCtaBanners?.map((b) => ({
                                        label: b.name,
                                        value: b.id,
                                      })) ?? []]}
                                    />
                                  }
                                  additionalContent={
                                    watch(`ctaBanners.${index}`) !== "" ? (
                                      <img src={ctaMedia?.cdnUrl}
                                        alt={`CTA Banner ${index + 1}`}
                                        className="h-48 object-cover rounded-md mt-2 mx-auto"
                                      />
                                    ) : (
                                      <CTAPlaceholder />
                                    )
                                  }
                                />
                              )
                            }
                            )}
                          </div>
                        )}
                      </ul>
                    )}
                    <ul className="border border-gray-200 bg-white rounded-md divide-y divide-gray-200 my-4">
                      <SingleLineSettingRow
                        label="Enable Property Section"
                        description="Your most recent properties will be added based on the 
client type  Sales, Lettings or Both. The default option is both."
                        value={
                          <StyledSwitchInput
                            register={register}
                            registerName="newsletterPropertyEnabled"
                            watch={watch}
                            setValue={setValue}
                            onChange={(checked) => {
                              if (!checked) {
                                setValue("ctaBanners.0", "");
                                setValue("ctaBanners.1", "");
                              }
                            }}
                          />
                        }
                      />
                      {watch("newsletterPropertyEnabled") && (
                        <MultiLineSettingRow
                          label="Number of Properties"
                          value={
                            <StyledSelectInput
                              classes="w-1/3 -mt-1"
                              register={register}
                              registerName="newsletterPropertyCount"
                              setValue={setValue}
                              watch={watch}
                              options={
                                Array.from({ length: 8 })
                                  .map((_, index) => ({
                                    label: (index + 1).toString(),
                                    value: (index + 1).toString()
                                  }))
                              }
                            />
                          }
                        />
                      )}
                    </ul>

                    <ul className="border border-gray-200 bg-white rounded-md divide-y divide-gray-200 my-4">
                      <SingleLineSettingRow
                        label="Bottom Call-To-Action Banner"
                        description="This will display a large banner toward the bottom of your newsletter"
                        value={
                          <StyledSwitchInput
                            register={register}
                            registerName="bottomCtaEnabled"
                            watch={watch}
                            setValue={setValue}
                            onChange={(checked) => {
                              if (!checked) {
                                setValue("ctaBanners.3", "");
                              }
                            }}
                          />
                        }
                      />

                      {watch("bottomCtaEnabled") && (() => {
                        const ctaMedia = media.data.find((m: any) => m.id === ctaBanners.data?.data.find((b) => b.id === watch(`ctaBanners.2`))?.mediaId);

                        return (
                          <SingleLineSettingRow
                            label={`Bottom CTA Banner`}
                            value={
                              <StyledSelectInput
                                classes="w-full"
                                register={register}
                                registerName={`ctaBanners.2`}
                                setValue={setValue}
                                watch={watch}
                                options={[{ label: "None", value: "" }, ...sortedCtaBanners?.map((b) => ({
                                  label: b.name,
                                  value: b.id,
                                })) ?? []]}
                              />
                            }
                            additionalContent={
                              watch(`ctaBanners.2`) !== "" ? (
                                <img src={ctaMedia?.cdnUrl}
                                  alt={`CTA Banner 3`}
                                  className="h-48 object-cover rounded-md mx-auto mt-2"
                                />
                              ) : (
                                <CTAPlaceholder recommendedResolution={[600, 500]} />
                              )
                            }
                          />
                        )
                      })()}
                    </ul>

                    <ul className="border border-gray-200 bg-white rounded-md divide-y divide-gray-200 my-4">
                      <SingleLineSettingRow
                        label="Enable Testimonials Section"
                        description="Display your latest testimonial on your newsletter"
                        value={
                          <StyledSwitchInput
                            register={register}
                            registerName="newsletterTestimonialEnabled"
                            watch={watch}
                            setValue={setValue}
                          />
                        }
                      />
                      {watch("newsletterTestimonialEnabled") && (
                        <MultiLineSettingRow
                          label="Number of Testimonials"
                          value={
                            <StyledSelectInput
                              classes="w-1/3 -mt-1"
                              register={register}
                              registerName="newsletterTestimonialCount"
                              setValue={setValue}
                              watch={watch}
                              options={
                                Array.from({ length: 4 })
                                  .map((_, index) => ({
                                    label: (index + 1).toString(),
                                    value: (index + 1).toString()
                                  }))
                              }
                            />
                          }
                        />
                      )}
                    </ul>
                    <div className="pb-4 pt-2 border-b">
                      <h3 className="text-base font-medium text-gray-900">
                        Marketing Groups
                      </h3>
                      <p className="mt-1 text-xs text-gray-500">
                        Choose the marketing groups you want to receive the newsletter.
                      </p>
                    </div>
                    <div>
                      <StyledMultiSelectInput
                        classes="w-1/2 mt-4"
                        register={register}
                        registerName="marketingGroups"
                        options={marketingGroups.data?.data.map((group) => ({
                          label: group.name,
                          value: group.id
                        })) || []}
                        setValue={setValue}
                        watch={watch}
                      />
                    </div>
                    <ul className="border border-gray-200 bg-white rounded-md divide-y divide-gray-200 my-4">
                      {watch("marketingGroups").map((groupId, index) => {
                        const matchedGroup = marketingGroups.data?.data.find((group) => group.id === groupId);

                        return (
                          <SingleLineSettingRow
                            key={index}
                            label={matchedGroup ? matchedGroup.name : `Unknown Group ${index + 1}`}
                            value={
                              !defaultGroups.includes(groupId) && (
                                <div className="flex items-center">
                                  <div className="ml-4 flex-shrink-0 flex items-center -mr-2">
                                    <button
                                      onClick={() => {
                                        setDeleteGroupId(groupId);
                                        setDeleteGroupOpen(true);
                                      }}
                                      className="mx-2 text-gray-400 hover:text-red-600"
                                    >
                                      <span className="sr-only">Delete</span>
                                      <TrashIcon className="h-6 w-6" aria-hidden="true" />
                                    </button>
                                  </div>
                                </div>
                              )
                            }
                          />
                        )
                      })}
                    </ul>
                    <div className="pb-4 pt-2 border-b">
                      <h3 className="text-base font-medium text-gray-900">
                        Sending Schedule
                      </h3>
                      <p className="mt-1 text-xs text-gray-500">
                        Choose the when the newsletter is sent out.
                      </p>
                    </div>
                    <div className="my-4">
                      <StyledRadioButtonGroupInput
                        registerName="newsletterSchedule"
                        watch={watch}
                        setValue={setValue}
                        options={[
                          {
                            label: "First Day",
                            value: NewsletterSchedule.FirstDay.toString(),
                          },
                          {
                            label: "Last Day",
                            value: NewsletterSchedule.LastDay.toString(),
                          },
                          {
                            label: "Custom",
                            value: NewsletterSchedule.SpecificDay.toString(),
                          }]}
                      />
                      {watch("newsletterSchedule") === NewsletterSchedule.SpecificDay.toString() && (
                        <div className="border border-gray-200 bg-white rounded-md p-4 my-4">
                          <div className="grid sm:grid-cols-3 gap-4">
                            <StyledSelectInput
                              label="Day of Month"
                              register={register}
                              setValue={setValue}
                              watch={watch}
                              registerName="newsletterScheduleDayOfMonth"
                              options={Array.from({ length: 31 }, (_, i) => ({
                                label: `${i + 1}`, value: `${i + 1}`
                              }))}
                            />
                            <StyledTimeInput
                              label="Time of Day"
                              register={register}
                              registerName="newsletterScheduleTime"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                ) : <></>
              }
            </>
          }
          <div className="flex justify-end mt-4 space-x-2">
            {/* @ts-ignore */}
            <ButtonInput label="Send Test Email" isSubmit={false} onClick={() => setSendTestOpen(true)} />
            {/* @ts-ignore */}
            <ButtonInput label="Save" isSubmit />
          </div>
        </form>
      </div >
    </>
  );
}
